import { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Avatar } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, MapPin, Globe, Loader2 } from 'lucide-react';
import { EventDetails } from '@/components/event-details';
import { detectTimeZone } from '@/lib/timezone';
import { getTimeSlots, getEventTypeById } from '@/client/sdk.gen';
import {
  EventTypeResponse,
  BookingResponse,
  OrganisationMemberResponse,
} from '@/client/types.gen';
import { BookingConfirmation } from '@/components/booking-confirmation';
import { DateTimeSelector } from '@/components/date-time-selector';

// Extend the BookingResponse type to include uid
interface ExtendedBookingResponse extends BookingResponse {
  uid: string;
}

export function BookingPage() {
  const [searchParams] = useSearchParams();
  const params = useParams();

  // Get data from either URL params or search params
  const urlUsername = params.username || '';
  const urlEventTypeSlug = params.eventTypeSlug || '';
  const queryEventTypeId = searchParams.get('eventTypeId');
  const queryUsername = searchParams.get('username') || '';

  // Determine which values to use - the username should come from URL or query params
  const username = urlUsername || queryUsername;

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    start: string;
    end: string;
  } | null>(null);
  const [eventTypeDetails, setEventTypeDetails] =
    useState<EventTypeResponse | null>(null);
  const [finalHosts, setFinalHosts] = useState<OrganisationMemberResponse[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [booking, setBooking] = useState<ExtendedBookingResponse | null>(null);
  const [timezone, setTimezone] = useState<string>(detectTimeZone());

  // Fetch event type details when component mounts
  useEffect(() => {
    async function fetchEventDetails() {
      setIsLoading(true);
      setError(null);

      try {
        // If event type ID is provided, use it to fetch event type details
        if (queryEventTypeId) {
          const response = await getEventTypeById({
            path: { event_type_id: queryEventTypeId },
          });

          if (response.error) {
            throw new Error(`Failed to fetch event type: ${response.error}`);
          }

          if (response.data) {
            setEventTypeDetails(response.data);
            // With direct event type fetch, we don't have final_event_type_hosts
            // Use event_type_hosts as fallback
            setFinalHosts(response.data.event_type_hosts || []);
          }
        }
        // If username and event type slug are provided, we'll get details from the first API call
        else if (username && urlEventTypeSlug) {
          // Set a default day to the current day to fetch the event type details
          const today = new Date();

          const response = await getTimeSlots({
            path: {
              username,
              event_type_slug: urlEventTypeSlug,
            },
            query: {
              year: today.getFullYear(),
              month: today.getMonth() + 1,
              day: today.getDate(), // Required day parameter
              timezone: timezone,
            },
          });

          if (response.error) {
            throw new Error(`Failed to fetch event details: ${response.error}`);
          }

          if (response.data) {
            setEventTypeDetails(response.data.event_type_details);
            // Use final_event_type_hosts for accurate host information
            setFinalHosts(response.data.final_event_type_hosts || []);
          }
        } else {
          throw new Error(
            'Event type ID or username/event type slug are required'
          );
        }
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load event details'
        );
      } finally {
        setIsLoading(false);
      }
    }

    fetchEventDetails();
  }, [queryEventTypeId, username, urlEventTypeSlug, timezone]);

  const handleTimeSelected = (
    date: Date,
    startTime: string,
    endTime: string
  ) => {
    setSelectedDate(date);
    setSelectedTimeSlot({ start: startTime, end: endTime });
  };

  const handleBookingCreated = (newBooking: ExtendedBookingResponse) => {
    setBooking(newBooking);
    // Scroll to top of page after booking is created
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDone = () => {
    window.location.href = '/';
  };

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto h-10 w-10 text-primary animate-spin" />
            <p className="mt-4 text-muted-foreground">
              Loading event details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-1 flex items-center justify-center">
          <div className="p-6 max-w-md rounded-lg border border-destructive/30 bg-destructive/10">
            <h2 className="text-xl font-semibold mb-4 text-destructive">
              Error
            </h2>
            <p className="text-muted-foreground">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (booking) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <BookingConfirmation booking={booking} onDone={handleDone} />
        </div>
      </div>
    );
  }

  if (!eventTypeDetails) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-1 flex items-center justify-center">
          <div className="p-6 max-w-md rounded-lg border border-destructive/30 bg-destructive/10">
            <h2 className="text-xl font-semibold mb-4 text-destructive">
              Error
            </h2>
            <p className="text-muted-foreground">No event details found</p>
          </div>
        </div>
      </div>
    );
  }

  const isCollectiveEvent = finalHosts.length > 1;

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
        <header className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-2">
            Book Your Appointment
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Select your preferred date and time to schedule a meeting with{' '}
            {isCollectiveEvent ? 'the team' : finalHosts[0]?.email || 'us'}.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-8 items-start justify-center">
          {/* Left column - Event details */}
          <div className="space-y-6 max-w-md mx-auto md:mx-0 w-full">
            <Card className="overflow-hidden border shadow-md group hover:shadow-lg transition-shadow">
              <div className="p-6 border-b bg-muted">
                <h2 className="text-2xl font-bold tracking-tight text-foreground">
                  {eventTypeDetails?.name}
                </h2>
                {eventTypeDetails?.description && (
                  <p className="mt-2 text-muted-foreground">
                    {eventTypeDetails.description}
                  </p>
                )}
              </div>

              <CardContent className="p-6 space-y-4">
                {finalHosts.length > 0 && (
                  <div className="pb-4 border-b border-border">
                    {isCollectiveEvent ? (
                      <div className="mb-2">
                        <p className="font-medium text-foreground">
                          Collective Event with:
                        </p>
                      </div>
                    ) : null}

                    {finalHosts.map((host, index) => (
                      <div
                        key={host.email}
                        className="flex items-center space-x-4 mb-3"
                      >
                        <Avatar className="h-12 w-12 border">
                          {host.profile_picture ? (
                            <img
                              src={host.profile_picture}
                              alt={host.email}
                              className="h-full w-full object-cover rounded-full"
                            />
                          ) : (
                            <div className="bg-primary text-primary-foreground flex items-center justify-center h-full w-full rounded-full text-xl font-semibold">
                              {host.email.charAt(0)}
                            </div>
                          )}
                        </Avatar>
                        <div>
                          <p className="font-medium text-foreground">
                            {host.email}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center p-3 rounded-lg bg-accent/50">
                    <Clock className="mr-3 h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Duration
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {eventTypeDetails?.duration} minutes
                      </p>
                    </div>
                  </div>

                  {eventTypeDetails?.locations &&
                    eventTypeDetails.locations.length > 0 && (
                      <div className="flex items-center p-3 rounded-lg bg-accent/50">
                        <MapPin className="mr-3 h-5 w-5 text-primary" />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Location
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {eventTypeDetails.locations[0].type}
                          </p>
                        </div>
                      </div>
                    )}

                  <div className="flex items-center p-3 rounded-lg bg-accent/50">
                    <Globe className="mr-3 h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Timezone
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {timezone}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {selectedDate && selectedTimeSlot && (
              <EventDetails
                date={selectedDate}
                startTime={selectedTimeSlot.start}
                endTime={selectedTimeSlot.end}
                eventTypeDetails={eventTypeDetails!}
                onBookingCreated={handleBookingCreated}
                timezone={timezone}
              />
            )}
          </div>

          {/* Right column - Calendar and time slots */}
          <DateTimeSelector
            username={username}
            eventTypeSlug={eventTypeDetails?.slug || urlEventTypeSlug}
            onTimeSelected={handleTimeSelected}
            initialTimezone={timezone}
          />
        </div>
      </div>
    </div>
  );
}
